// apps/client/src/components/rdti/RDTIFormRenderer.tsx
//
// Dynamic form renderer for an RDTI schema section. Given a schemaVersion +
// section + responses map, it iterates the FieldDef[] from
// @avanzu/rdti-schema and renders the right MUI input per inputType,
// honours skip logic (conditionalOn), enforces maxChars caps + minChars
// floors with live counters, and surfaces compliance scoring inline.
//
// Structural columns on `projects` / `researchActivities` (name, taxYear,
// totalBudget, status, etc.) are owned by their parent form — the renderer
// only handles fields stored inside `responses`. Fields whose `key` matches
// an entry in `excludeKeys` are skipped so the parent can render them once
// (e.g. FY25 `project.name` is the same column as `projects.name`).

import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  type FieldDef,
  type RDTIResponses,
  type RDTISection,
  getFieldsForSection,
  scoreField,
} from '@avanzu/rdti-schema';
import { getComplianceColor } from '../../utils/compliance';

export interface RDTIFormRendererProps {
  schemaVersion: string;
  section: RDTISection;
  responses: RDTIResponses;
  onChange: (next: RDTIResponses) => void;
  /** FieldDef.key values to skip — handled by parent's structural form. */
  excludeKeys?: string[];
  /** Runtime option lists for dropdown/checkbox fields that the schema declares with empty options[] (sibling activities, ANZSRC codes, etc.). */
  dynamicOptions?: Record<string, FieldDef['options']>;
  disabled?: boolean;
  /**
   * Optional slot rendered above a specific field (used to inject
   * AiFieldEnhancer). Only invoked for narrative inputs (`text` / `richText`)
   * — accessories on radio / dropdown / currency / etc. would be nonsense, so
   * the renderer suppresses them centrally rather than asking every caller.
   */
  renderFieldAccessory?: (field: FieldDef) => React.ReactNode;
}

const NARRATIVE_INPUT_TYPES = new Set<FieldDef['inputType']>(['text', 'richText']);

function isVisible(field: FieldDef, responses: RDTIResponses): boolean {
  if (!field.conditionalOn) return true;
  const actual = responses[field.conditionalOn.field];
  const expected = field.conditionalOn.equals;
  if (Array.isArray(expected)) return expected.includes(String(actual));
  if (Array.isArray(actual)) return (actual as unknown[]).map(String).includes(expected);
  return String(actual) === expected;
}

export const RDTIFormRenderer: React.FC<RDTIFormRendererProps> = ({
  schemaVersion,
  section,
  responses,
  onChange,
  excludeKeys,
  dynamicOptions,
  disabled,
  renderFieldAccessory,
}) => {
  const fields = useMemo(() => {
    const exclude = new Set(excludeKeys ?? []);
    return getFieldsForSection(schemaVersion, section).filter((f) => !exclude.has(f.key));
  }, [schemaVersion, section, excludeKeys]);

  const setValue = (key: string, value: unknown) => onChange({ ...responses, [key]: value });

  return (
    <Stack spacing={3}>
      {fields.map((field) => {
        if (!isVisible(field, responses)) return null;
        const value = responses[field.key];
        const options = (dynamicOptions?.[field.key] ?? field.options) ?? [];

        return (
          <Box key={field.key}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {field.questionNumber > 0 && (
                  <Box component="span" sx={{ mr: 1, color: 'text.disabled', fontVariant: 'tabular-nums' }}>
                    Q{field.questionNumber}
                  </Box>
                )}
                {field.label}
                {field.required && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}
              </Typography>
              {NARRATIVE_INPUT_TYPES.has(field.inputType) && renderFieldAccessory?.(field)}
            </Box>
            {field.helpText && (
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 0.5 }}>
                {field.helpText}
              </Typography>
            )}

            <RDTIFieldInput
              field={field}
              value={value}
              options={options}
              disabled={disabled}
              onChange={(v) => setValue(field.key, v)}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

interface FieldInputProps {
  field: FieldDef;
  value: unknown;
  options: NonNullable<FieldDef['options']>;
  disabled?: boolean;
  onChange: (v: unknown) => void;
}

const RDTIFieldInput: React.FC<FieldInputProps> = ({ field, value, options, disabled, onChange }) => {
  switch (field.inputType) {
    case 'text':
    case 'richText': {
      const text = typeof value === 'string' ? value : '';
      const score = scoreField(field, text);
      const tooShort = field.minChars != null && text.length > 0 && text.length < field.minChars;
      const overCap = field.maxChars != null && text.length > field.maxChars;
      const color = getComplianceColor(score.score);
      const helper = field.complianceRules
        ? [
            field.minChars ? `min ${field.minChars}` : null,
            field.maxChars ? `max ${field.maxChars}` : null,
            score.hasRedFlags ? `⚠ ${score.redFlags.join(', ')}` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : field.maxChars
          ? `max ${field.maxChars} characters`
          : undefined;
      return (
        <>
          <TextField
            fullWidth
            value={text}
            onChange={(e) => onChange(e.target.value)}
            multiline={field.inputType === 'richText'}
            minRows={field.inputType === 'richText' ? 4 : undefined}
            error={overCap || score.hasRedFlags}
            disabled={disabled}
            helperText={
              <Box component="span" display="flex" justifyContent="space-between">
                <Box component="span">{helper}</Box>
                <Box
                  component="span"
                  sx={{
                    color: overCap ? 'error.main' : tooShort ? 'warning.main' : 'text.disabled',
                    fontVariant: 'tabular-nums',
                  }}
                >
                  {text.length}
                  {field.maxChars ? ` / ${field.maxChars}` : ''}
                </Box>
              </Box>
            }
            inputProps={{ maxLength: field.maxChars }}
          />
          {field.complianceRules && text.length > 0 && (
            <Box mt={0.5}>
              <Chip
                size="small"
                label={`compliance: ${score.score}`}
                color={color}
                variant="outlined"
              />
            </Box>
          )}
        </>
      );
    }
    case 'radio': {
      return (
        <RadioGroup
          row
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio disabled={disabled} />}
              label={opt.label}
            />
          ))}
        </RadioGroup>
      );
    }
    case 'checkbox': {
      const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <FormGroup>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={selected.has(opt.value)}
                  disabled={disabled}
                  onChange={(_, checked) => {
                    const next = new Set(selected);
                    if (checked) next.add(opt.value);
                    else next.delete(opt.value);
                    onChange(Array.from(next));
                  }}
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>
      );
    }
    case 'dropdown': {
      return (
        <FormControl fullWidth>
          <InputLabel>{field.label}</InputLabel>
          <Select
            label={field.label}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    case 'currency': {
      const num = typeof value === 'number' ? value : '';
      return (
        <TextField
          type="number"
          value={num}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          disabled={disabled}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      );
    }
    case 'date': {
      const text = typeof value === 'string' ? value : '';
      return (
        <TextField
          type="date"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />
      );
    }
    case 'dateRange': {
      const range = (value as { start?: string; end?: string } | undefined) ?? {};
      return (
        <Stack direction="row" spacing={2}>
          <TextField
            label="Start"
            type="date"
            value={range.start ?? ''}
            onChange={(e) => onChange({ ...range, start: e.target.value })}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End"
            type="date"
            value={range.end ?? ''}
            onChange={(e) => onChange({ ...range, end: e.target.value })}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      );
    }
    case 'multiCurrency': {
      const entries = Array.isArray(value) ? (value as Array<{ year: string; amount: number | null }>) : [];
      return (
        <Stack spacing={1}>
          {entries.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              No years yet — add one below.
            </Typography>
          )}
          {entries.map((entry, idx) => (
            <Stack direction="row" spacing={2} key={idx}>
              <TextField
                label="Year"
                value={entry.year}
                onChange={(e) => {
                  const next = [...entries];
                  next[idx] = { ...next[idx], year: e.target.value };
                  onChange(next);
                }}
                disabled={disabled}
                sx={{ width: 120 }}
              />
              <TextField
                label="Amount"
                type="number"
                value={entry.amount ?? ''}
                onChange={(e) => {
                  const next = [...entries];
                  next[idx] = { ...next[idx], amount: e.target.value === '' ? null : Number(e.target.value) };
                  onChange(next);
                }}
                disabled={disabled}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Stack>
          ))}
          <FormHelperText
            onClick={() => onChange([...entries, { year: '', amount: null }])}
            sx={{ cursor: 'pointer', color: 'primary.main' }}
          >
            + Add year
          </FormHelperText>
        </Stack>
      );
    }
    case 'abnLookup': {
      const v = (value as { abn?: string; name?: string } | undefined) ?? {};
      return (
        <Stack direction="row" spacing={2}>
          <TextField
            label="ABN / ACN / ARBN"
            value={v.abn ?? ''}
            onChange={(e) => onChange({ ...v, abn: e.target.value })}
            disabled={disabled}
          />
          <TextField
            label="Entity name"
            value={v.name ?? ''}
            onChange={(e) => onChange({ ...v, name: e.target.value })}
            disabled={disabled}
            sx={{ flex: 1 }}
          />
        </Stack>
      );
    }
    case 'addressLookup': {
      const a = (value as { line1?: string; suburb?: string; state?: string; postcode?: string } | undefined) ?? {};
      return (
        <Stack spacing={1}>
          <TextField
            label="Address line"
            value={a.line1 ?? ''}
            onChange={(e) => onChange({ ...a, line1: e.target.value })}
            disabled={disabled}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label="Suburb"
              value={a.suburb ?? ''}
              onChange={(e) => onChange({ ...a, suburb: e.target.value })}
              disabled={disabled}
              sx={{ flex: 2 }}
            />
            <TextField
              label="State"
              value={a.state ?? ''}
              onChange={(e) => onChange({ ...a, state: e.target.value })}
              disabled={disabled}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Postcode"
              value={a.postcode ?? ''}
              onChange={(e) => onChange({ ...a, postcode: e.target.value })}
              disabled={disabled}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      );
    }
    default:
      return <Alert severity="warning">Unsupported field type: {field.inputType}</Alert>;
  }
};
